import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { axiosConfig } from "../../axios/axiosConfig";
import toast from "react-hot-toast";

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });

const BrokerAccessTable = ({ refetchKey = 0 }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const fetchBrokerAccessList = async () => {
      setLoading(true);
      try {
        const res = await axiosConfig({ method: "get", url: "/appdashboard/get-broker-access/" });
        setRows(res?.data?.data || []);
      } catch (e) {
        const errMsg =
          e.response?.data?.message ||
          e.response?.data?.detail ||
          e.message ||
          "Failed to load broker access list";
        toast.error(errMsg);
      } finally {
        setLoading(false);
      }
    };
    fetchBrokerAccessList();
  }, [refetchKey]);

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      const res = await axiosConfig({
        method: "delete",
        url: "/appdashboard/remove-broker-access/",
        data: { phone: deleteRow.phone },
      });
      const msg = res?.data?.message || res?.data?.response || "Broker access removed!";
      toast.success(msg);
      setRows((prev) => prev.filter((r) => r.id !== deleteRow.id));
      setDeleteRow(null);
    } catch (e) {
      const errMsg =
        e.response?.data?.message ||
        e.response?.data?.response ||
        e.response?.data?.detail ||
        e.message ||
        "Failed to remove broker access";
      toast.error(errMsg);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          background: "#fff",
          boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            background: "#04343a",
            px: 2.5, py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 13, letterSpacing: 0.5 }}>
            BROKER ACCESS LIST
          </Typography>
          {!loading && (
            <Typography sx={{ color: "rgba(255,255,255,0.55)", fontSize: 12 }}>
              {rows.length} record{rows.length !== 1 ? "s" : ""}
            </Typography>
          )}
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {["S.No", "User ID", "Username", "Phone", "Broker ID", "Created At", "Action"].map((h) => (
                  <TableCell
                    key={h}
                    sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", py: 1.2, whiteSpace: "nowrap", background: "#f8fafc" }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: "center", py: 6 }}>
                    <CircularProgress size={28} sx={{ color: "#04343a" }} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: "center", py: 5, fontSize: 13, color: "#94a3b8" }}>
                    No broker access records found
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, i) => (
                  <TableRow key={row.id} sx={{ "&:hover": { background: "#f8fafc" } }}>
                    <TableCell sx={{ fontSize: 12, color: "#64748b" }}>{i + 1}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: "#64748b" }}>{row.user_id}</TableCell>
                    <TableCell sx={{ fontSize: 13, fontWeight: 600, color: "#0f172a" }}>{row.username}</TableCell>
                    <TableCell sx={{ fontSize: 13, color: "#0f172a" }}>{row.phone}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: "#64748b" }}>{row.broker_id}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: "#64748b", whiteSpace: "nowrap" }}>
                      {formatDate(row.created_at)}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => setDeleteRow(row)}
                        sx={{ color: "#dc2626", "&:hover": { background: "#fef2f2" } }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteRow} onClose={() => setDeleteRow(null)} PaperProps={{ sx: { borderRadius: "14px", minWidth: 360 } }}>
        <Box sx={{ px: 3, py: 2, background: "#04343a" }}>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>Remove Broker Access</Typography>
        </Box>
        <DialogContent sx={{ pt: 2.5 }}>
          <Typography sx={{ fontSize: 13, color: "#64748b" }}>
            Are you sure you want to remove broker access for{" "}
            <strong style={{ color: "#0f172a" }}>{deleteRow?.username}</strong>{" "}
            ({deleteRow?.phone})?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setDeleteRow(null)}
            size="small"
            sx={{ borderRadius: "8px", textTransform: "none", color: "#64748b" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleDelete}
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
            sx={{
              borderRadius: "8px",
              background: "#dc2626",
              textTransform: "none",
              fontWeight: 600,
              px: 2.5,
              "&:hover": { background: "#b91c1c" },
            }}
          >
            {deleteLoading ? "Removing…" : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BrokerAccessTable;
